import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarDays, CheckCircle2, Timer, Users2, Zap, FileText, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivityFeed = ({ getAuthHeaders, isGuestMode }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isGuestMode) {
      setItems([
        { id: 'demo-1', type: 'event_created', title: "Jude's Pool Party", description: 'Event created in Fort Lauderdale, FL', timestamp: new Date().toISOString(), icon: 'calendar' },
        { id: 'demo-2', type: 'hold_placed', title: 'Summer Kickoff', description: 'Hold #1 on 2026-06-15', timestamp: new Date(Date.now() - 3600000).toISOString(), icon: 'clock' },
        { id: 'demo-3', type: 'task_completed', title: 'Confirm DJ booking', description: 'Task completed', timestamp: new Date(Date.now() - 7200000).toISOString(), icon: 'list' },
      ]);
      setLoading(false);
      return;
    }
    const fetchFeed = async () => {
      try {
        const res = await axios.get(`${API}/activity-feed?limit=10`, { headers: getAuthHeaders() });
        setItems(res.data.items || []);
      } catch { /* silently fail */ }
      finally { setLoading(false); }
    };
    fetchFeed();
  }, [getAuthHeaders, isGuestMode]);

  const iconMap = {
    calendar: CalendarDays,
    check: CheckCircle2,
    clock: Timer,
    users: Users2,
    zap: Zap,
    list: FileText,
  };

  const colorMap = {
    event_created: 'text-blue-500 bg-blue-50 dark:bg-blue-950/30',
    event_published: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
    hold_placed: 'text-amber-500 bg-amber-50 dark:bg-amber-950/30',
    hold_released: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
    hold_expired: 'text-red-500 bg-red-50 dark:bg-red-950/30',
    circle_created: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-950/30',
    group_created: 'text-teal-500 bg-teal-50 dark:bg-teal-950/30',
    private_event_created: 'text-violet-500 bg-violet-50 dark:bg-violet-950/30',
    task_completed: 'text-green-500 bg-green-50 dark:bg-green-950/30',
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/30" data-testid="activity-feed-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {items.map((item) => {
            const Icon = iconMap[item.icon] || CalendarDays;
            const color = colorMap[item.type] || 'text-slate-500 bg-slate-100 dark:bg-slate-800';
            return (
              <div key={item.id} className="flex items-start gap-2.5 py-2 border-b border-border/50 last:border-0" data-testid={`activity-item-${item.id}`}>
                <div className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${color}`}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{item.title}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{item.description}</p>
                </div>
                <span className="text-[10px] text-muted-foreground flex-shrink-0 mt-0.5">{formatTime(item.timestamp)}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
